import { useEffect, useRef } from 'react';

/** Normalize and split a 6-digit OTP into single-character boxes. */
export function applyOtpDigits(rawValue, setOtp) {
  const digits = String(rawValue || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length !== 6) return false;
  setOtp(digits.split(''));
  return true;
}

/**
 * Browser OTP autofill for the mobile web journey.
 * - Chrome/Android: WebOTP API (navigator.credentials.get)
 * - iOS Safari: autocomplete="one-time-code" on the hidden capture input
 *
 * Requires MSG91 SMS template last line: @yourdomain.com #123456
 * (domain must match window.location.hostname).
 */
export function useOtpAutofill(setOtp, { enabled = true } = {}) {
  const abortRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const ac = new AbortController();
    abortRef.current = ac;

    const supportsWebOtp =
      'OTPCredential' in window &&
      typeof navigator.credentials?.get === 'function';

    if (supportsWebOtp) {
      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        })
        .then((credential) => {
          if (credential?.code) {
            applyOtpDigits(credential.code, setOtp);
          }
        })
        .catch(() => {
          // Dismissed, timed out, or SMS not in WebOTP format — fall back to manual entry.
        });
    }

    return () => {
      ac.abort();
      abortRef.current = null;
    };
  }, [enabled, setOtp]);
}
